import { supabase } from "./core.js";

interface TaxReport {
  id?: string;
  client_id: string;
  year: number;
  file_hash?: string;
  status?: string;
  [key: string]: unknown;
}

interface TaxReportItem {
  id?: string;
  report_id: string;
  [key: string]: unknown;
}

interface TaxReportTotals {
  id?: string;
  report_id: string;
  [key: string]: unknown;
}

export async function fetchTaxReports(clientId: string): Promise<TaxReport[]> {
  const { data, error } = await supabase
    .from("tax_reports")
    .select("*")
    .eq("client_id", clientId)
    .order("year", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function fetchTaxReportItems(reportId: string): Promise<TaxReportItem[]> {
  const { data, error } = await supabase
    .from("tax_report_items")
    .select("*")
    .eq("report_id", reportId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function fetchTaxReportTotals(reportId: string): Promise<TaxReportTotals | null> {
  const { data, error } = await supabase
    .from("tax_report_totals")
    .select("*")
    .eq("report_id", reportId)
    .single();
  if (error) {
    if (error.code === "PGRST116") return null;
    throw error;
  }
  return data;
}

export async function upsertTaxReport(
  report: Partial<TaxReport>,
  items: Partial<TaxReportItem>[],
  totals: Partial<TaxReportTotals>
): Promise<string> {
  const { data: reportData, error: reportErr } = await supabase
    .from("tax_reports")
    .upsert(report, { onConflict: "id" })
    .select("id")
    .single();
  if (reportErr) throw reportErr;

  const reportId = reportData.id as string;

  if (items.length > 0) {
    const rows = items.map((it) => ({ ...it, report_id: reportId }));
    const { error: itemsErr } = await supabase
      .from("tax_report_items")
      .upsert(rows, { onConflict: "id" });
    if (itemsErr) throw itemsErr;
  }

  if (totals) {
    const { error: totalsErr } = await supabase
      .from("tax_report_totals")
      .upsert({ ...totals, report_id: reportId }, { onConflict: "report_id" });
    if (totalsErr) throw totalsErr;
  }

  return reportId;
}

export async function taxReportExistsByHash(fileHash: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("tax_reports")
    .select("id")
    .eq("file_hash", fileHash)
    .limit(1);
  if (error) return false;
  return (data?.length || 0) > 0;
}
